import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PrimaryButton } from '../connectedForm/Button';
import {
  Button,
  CounterBadge,
  createTableColumn,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  TableRowId,
  Text,
} from '@fluentui/react-components';
import { SubtleWarningButton } from './FilterBase';
import { ModalBottomButtonContainer } from '../modals/Styles';
import { DocumentNode } from 'graphql';
import {
  Direction,
  FilterOperation,
  IEditableDataGridView,
  IFilterInput,
  IFilterObjectInput,
} from '../../graphql/schema/p4types';
import {
  ICommonRequestResult,
  ICommonRequestVariables,
  QUERY_PAGE_SIZE,
} from '../FluentWrapper/useQueryForDetailsList';
import { UIFilterType } from '../../utils/FilterHelper';
import { IDropdownOptionRenderer, IRemoteFilterGetter } from '../../@types/entityTypes';
import { mergeWith } from 'lodash';
import { mergeCustomizerArrayConcat } from '../../graphql/hooks/useWorkspace';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { FluentTable, IFluentTableRowSize } from './FluentViewTable';
import { UseTableFeaturesOptions } from '@fluentui/react-table';
import WorkspaceContext from '../Context/WorkspaceContext';

type IExtendedFilterObject = IFilterObjectInput & {
  fieldColumn: any;
  objects: Array<{ id: number }>; //May contain other content, but believe this is all we technically need.
  position: number;
  type: string;
};

const FilterTable = ({
  dismissFilter,
  fieldColumn,
  fieldName,
  label,
  objects,
  position,
  query,
  'data-testid': testid,
  type,
  onFilterUpdated,
  id,
  labelKey,
  remoteFilterGetter,
  optionRenderer,
  styles,
  hideRemoveButton,
  customVariables,
}: {
  dismissFilter: (filter: IFilterObjectInput) => void;
  fieldColumn?: { fieldName: string };
  fieldName: string;
  label: string;
  objects?: Array<any>;
  position: number;
  query: DocumentNode;
  type: UIFilterType;
  onFilterUpdated: (filter: IExtendedFilterObject) => void;
  id?: number; //filter id (if persisted, I assume)
  'data-testid': string;
  remoteFilterGetter?: IRemoteFilterGetter;
  labelKey: string; //This comes from the dropdown config for the entity
  optionRenderer: IDropdownOptionRenderer; //This comes from the dropdown config for the entity
  styles?: {
    buttonText?: React.CSSProperties;
  };
  hideRemoveButton?: boolean;
  customVariables?: (options: { rowData?: any; column: unknown }) => any;
}) => {
  const [remoteFilters, setRemoteFilters] = useState<Array<IFilterInput>>([]);

  const customDropdownVariables = customVariables?.({ rowData: undefined, column: fieldColumn });

  const baseVariables: ICommonRequestVariables = {
    filters: remoteFilters,
    first: QUERY_PAGE_SIZE,
  };

  const mergedVariables = customDropdownVariables
    ? mergeWith(customDropdownVariables, baseVariables, mergeCustomizerArrayConcat)
    : baseVariables;

  //Local state for selected options - Filter isn't persisted until user clicks 'update'
  const [localSelectedOptionsIds, setLocalSelectedOptionsIds] = useState<Set<TableRowId>>(new Set());

  //Maintain a map of all fetched items so we can easily access the full objects for reference in filters.
  const [allItemsById, setAllItemsById] = useState<{ [key: number]: any }>({});

  const selectedItemFilters = useMemo<Array<IFilterInput>>(() => {
    const missingIds =
      objects?.map((object) => object.id).filter((id) => !Object.values(allItemsById).find((item) => item.id === id)) ??
      [];
    const missingIdsFromLocalSelections = Array.from(localSelectedOptionsIds).filter(
      (id) => !allItemsById[id as number]
    );

    return (
      [...missingIds, ...missingIdsFromLocalSelections].map((id) => {
        const idFilter: IFilterInput = {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'id',
            value: `${id}`,
          },
        };
        return idFilter;
      }) ?? []
    );
  }, [allItemsById, localSelectedOptionsIds, objects]);

  //Fetch any selected items and add them to our local map (Which we need so that we know the reference/description for selected items for display).
  useQuery<ICommonRequestResult, ICommonRequestVariables>(query, {
    variables: { filters: selectedItemFilters },
    skip: selectedItemFilters?.length === 0,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (dataResult) => {
      setAllItemsById((prev) => {
        const newObj = { ...prev };
        dataResult.list.edges?.forEach((value) => {
          newObj[value.node.id] = value.node;
        });
        return newObj;
      });
    },
  });

  const { data, fetchMore } = useQuery<ICommonRequestResult, ICommonRequestVariables>(query, {
    variables: mergedVariables,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (dataResult) => {
      if (dataResult.list.edges?.some((value) => value && !allItemsById[value.node.id])) {
        setAllItemsById((prev) => {
          const newObj = { ...prev };
          dataResult.list.edges?.forEach((value) => {
            if (value) {
              newObj[value.node.id] = value.node;
            }
          });
          return newObj;
        });
      }
    },
  });

  const options = useMemo<Array<any>>(() => {
    return data?.list?.edges?.map((edge: { node?: any }) => edge?.node as any) ?? [];
  }, [data?.list?.edges]);

  //Update local selected options if options selected by-props changes. (+ Reset 'Previous Selected' list).
  useEffect(() => {
    setLocalSelectedOptionsIds(new Set(objects?.map((object) => object.id) ?? []));
  }, [objects]);

  const updateFilterData = () => {
    const selectedItems = Array.from(localSelectedOptionsIds).map((id) => allItemsById[id as number]);

    onFilterUpdated({
      id: id,
      codes: selectedItems.map((value) => value.id),
      fieldColumn: fieldColumn,
      fieldName: fieldName,
      objects: selectedItems,
      position: position,
      type: type,
    });
  };

  const filterText = useMemo(() => {
    const selectedOptions = objects?.map((object) => allItemsById[object.id]).filter((value) => !!value) ?? [];
    return selectedOptions.length > 0
      ? selectedOptions.map((value) => value[labelKey || 'description']).join(', ')
      : label;
  }, [allItemsById, label, labelKey, objects]);
  const columns = useMemo(() => {
    const renderCell = (item: any) => {
      if (!optionRenderer) return item[labelKey] ?? item;
      const OptionRenderer = optionRenderer;
      return (
        <div style={{ overflow: 'clip' }}>
          <OptionRenderer option={item} />
        </div>
      );
    };

    return [
      createTableColumn<IEditableDataGridView>({
        columnId: labelKey || 'description',
        renderCell: renderCell,
      }),
    ];
  }, [labelKey, optionRenderer]);

  const tableFeatureOptions = useMemo<UseTableFeaturesOptions<any>>(
    () => ({
      columns: columns,
      items: options,
    }),
    [columns, options]
  );

  const loadedPagesRef = useRef<{ [key: number]: boolean }>({ 0: true });

  const isItemLoaded = useCallback((index: number) => {
    return loadedPagesRef.current[(index / QUERY_PAGE_SIZE) >> 0];
  }, []);

  const loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void = (startIndex, stopIndex) => {
    const startPage = (startIndex / QUERY_PAGE_SIZE) >> 0;
    const stopPage = (stopIndex / QUERY_PAGE_SIZE) >> 0;
    const promises: Array<Promise<ApolloQueryResult<any>>> = [];
    for (let i = startPage; i <= stopPage; i++) {
      if (!loadedPagesRef.current[i]) {
        promises.push(
          fetchMore({
            variables: {
              first: QUERY_PAGE_SIZE,
              after: i * QUERY_PAGE_SIZE,
            },
          })
        );
        loadedPagesRef.current[i] = true;
      }
    }
    return Promise.all(promises) as unknown as Promise<void>;
  };

  return (
    <Popover positioning={'below'}>
      <PopoverTrigger disableButtonEnhancement>
        <Button appearance={'transparent'} style={{ borderRight: 'solid 1px #e0e0e0' }}>
          <Text
            style={{ paddingRight: '5px', maxWidth: '150px', ...(styles?.buttonText || {}) }}
            truncate={true}
            wrap={false}
          >
            {filterText}
          </Text>
          <CounterBadge count={objects?.length ?? 0} style={{ backgroundColor: '#00b9af' }} />
        </Button>
      </PopoverTrigger>
      <PopoverSurface style={{ padding: '0 0 60px 0' }}>
        <div
          style={{
            width: '350px',
            height: '310px',
            paddingBottom: '50px',
            position: 'relative',
            borderBottom: 'lightgrey 1px solid',
          }}
        >
          <FluentTable<any>
            itemCount={data?.list?.count ?? 0}
            height={246}
            tableFeatureOptions={tableFeatureOptions}
            selectionMode={'multiselect'}
            selectionKey={'id'}
            onSelectionChange={(ev, data) => setLocalSelectedOptionsIds(data.selectedItems)}
            selectedItems={localSelectedOptionsIds}
            size={IFluentTableRowSize.ExtraSmall}
            infiniteScrollConfig={{
              isItemLoaded,
              loadMoreItems,
              setRemoteFilters,
              remoteFilterGetter,
            }}
          />
        </div>
        <ModalBottomButtonContainer>
          <PrimaryButton small onClick={updateFilterData} data-testid={`${testid}_update`} style={{ flexGrow: 0.33 }}>
            Update
          </PrimaryButton>
          {hideRemoveButton ? null : (
            <SubtleWarningButton small style={{ flexGrow: 0.33 }} onClick={() => dismissFilter({ fieldName })}>
              Remove
            </SubtleWarningButton>
          )}
        </ModalBottomButtonContainer>
      </PopoverSurface>
    </Popover>
  );
};

export default FilterTable;
